import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getView, // MODIFIED: Replaced retrieveViews with getView
  getProjectDetails,
  updateViewTitle
} from "../services/database";
import Navbar5 from "../components/Navbar5";
import DeleteVersion from "../components/DeleteVersion";
import GenerationLimitModal from "../components/GenerationLimitModal";
import { canUserGenerate } from "../services/subscriptionCheck";
import ShareModal from "../components/ShareModal";
import { useServiceStatus } from "../context/ServiceStatusContext";
import { RefreshCcw, Maximize2, X } from "lucide-react";
import DesktopWarningBanner from "../components/DesktopWarningBanner";

// Helper that injects custom scrollbar CSS into HTML docs shown inside the iframe
const injectScrollbarStyles = (htmlContent) => {
  const scrollbarStyles = `
    <style>
      ::-webkit-scrollbar {
        width: 6px;
      }
      ::-webkit-scrollbar-track {
        background: transparent;
      }
      ::-webkit-scrollbar-thumb {
        background-color: rgba(100, 100, 100, 0.5);
        border-radius: 4px;
      }
      ::-webkit-scrollbar-thumb:hover {
        background-color: rgba(100, 100, 100, 0.7);
      }
      * {
        scrollbar-width: thin;
        scrollbar-color: rgba(100, 100, 100, 0.5) transparent;
      }
    </style>
  `;

  if (htmlContent.includes("<head>")) {
    return htmlContent.replace("<head>", "<head>" + scrollbarStyles);
  }
  return htmlContent.replace("<html>", "<html><head>" + scrollbarStyles + "</head>");
};

// MODIFIED ViewDetails.js injectRateLimitReporter function
const injectRateLimitReporter = (htmlContent) => {
  const reporterScript = `
    <script>
      (function(){
        function setupRateLimitReporter() {
          const API_MARKER = '/api/v1/images/';
          window.addEventListener('error', function(e){
            const img = e.target;
            if (img && img.tagName === 'IMG' && img.src && img.src.includes(API_MARKER)) {
              fetch(img.src, { method: 'HEAD', cache: 'no-store' })
                .then(r => {
                  if (r.status === 429) {
                    if (parent && parent.postMessage) {
                      parent.postMessage(
                        { type: 'IMAGE_RATE_LIMIT', src: img.src },
                        '*'
                      );
                    }
                  }
                })
                .catch(() => {});
            }
          }, true);
        }
        
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', setupRateLimitReporter);
        } else {
          setupRateLimitReporter();
        }
      })();
    </script>
  `;

  if (htmlContent.includes('</body>')) {
    return htmlContent.replace('</body>', reporterScript + '</body>');
  }
  return htmlContent + reporterScript;
};

// ─── Gray 1×1 SVG placeholder used when downloading ────────────────
const GRAY_PLACEHOLDER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' " +
  "width='100%25' height='100%25' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E%3C/svg%3E";

const ViewDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { serviceDown } = useServiceStatus();

  const [showLimitModal, setShowLimitModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState("trial");

  // Router-state params
  const { projectId, viewId, currentVersionIndex: routeVersionIndex } = location.state || {};
  
  useEffect(() => {
    console.log('ViewDetails received state:', { 
      projectId, 
      viewId, 
      hasRouteVersionIndex: routeVersionIndex !== undefined, 
      routeVersionIndex,
      pathname: location.pathname 
    });
  }, [projectId, viewId, routeVersionIndex, location.pathname]);

  const [view, setView] = useState(null);
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deviceChoice, setDeviceChoice] = useState("Desktop");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [restartLoading, setRestartLoading] = useState(false);

  // Fullscreen modal state and hover state
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [isIframeHovered, setIsIframeHovered] = useState(false);
  const [mobileContainerWidth, setMobileContainerWidth] = useState(null);

  /* ------------------------------------------------------------------ */
  /* DATA FETCH (MODIFIED)                                             */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!projectId) {
      setError("Project ID is missing.");
      setLoading(false);
      return;
    }
    if (!viewId) {
      setError("View ID is missing.");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        // Efficiently fetch project and the specific view in parallel
        const [project, matchedView] = await Promise.all([
          getProjectDetails(projectId),
          getView(viewId),
        ]);

        if (!project) throw new Error("Project not found.");
        if (!matchedView) throw new Error("View not found.");

        if (
          project.styleChoices &&
          project.styleChoices.device &&
          project.styleChoices.device.toLowerCase() === "mobile"
        ) {
          setDeviceChoice("Mobile");
        }

        // Filter out incomplete versions
        matchedView.versions = matchedView.versions.filter(
          (v) => !v.revisionStatus || v.revisionStatus === "complete"
        );
        if (matchedView.versions.length === 0) {
          throw new Error("No completed versions available for this view.");
        }

        setView(matchedView);
        
        // Correctly set version index to route param or the latest version
        if (routeVersionIndex !== undefined && routeVersionIndex >= 0 && 
            routeVersionIndex < matchedView.versions.length) {
          console.log(`Setting currentVersionIndex to ${routeVersionIndex} from route state`);
          setCurrentVersionIndex(routeVersionIndex);
        } else {
          console.log(`Setting currentVersionIndex to latest (${matchedView.versions.length - 1})`);
          setCurrentVersionIndex(matchedView.versions.length - 1);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, viewId, routeVersionIndex]);

  /* ------------------------------------------------------------------ */
  /* TITLE RENAME HANDLER                                              */
  /* ------------------------------------------------------------------ */
  const handleTitleSave = async (newTitle) => {
    if (!view) return;
    try {
      await updateViewTitle(view._id, newTitle);
      setView({ ...view, title: newTitle });
    } catch (err) {
      console.error("Error renaming view:", err);
      // TODO: surface a toast/alert for the user
    }
  };

  /* ------------------------------------------------------------------ */
  /* NAVIGATION BETWEEN VERSIONS                                       */
  /* ------------------------------------------------------------------ */
  const handlePrev = () => {
    if (currentVersionIndex > 0) {
      setCurrentVersionIndex((idx) => idx - 1);
    }
  };

  const handleNext = () => {
    if (view && view.versions && currentVersionIndex < view.versions.length - 1) {
      setCurrentVersionIndex((idx) => idx + 1);
    }
  };

  /* ------------------------------------------------------------------ */
  /* DOWNLOAD CURRENT VERSION                                          */
  /* ------------------------------------------------------------------ */
  const handleDownload = () => {
    if (!view?.versions?.length) return;
    const currentVersion = view.versions[currentVersionIndex];
    if (!currentVersion?.html) return;
  
    const parser = new DOMParser();
    const doc = parser.parseFromString(currentVersion.html, "text/html");
  
    doc.querySelectorAll("img[src]").forEach((img) => {
      img.setAttribute("src", GRAY_PLACEHOLDER);
    });
  
    const processed = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
    const blob = new Blob([processed], { type: "text/html" });
    const url  = URL.createObjectURL(blob);
  
    const a = document.createElement("a");
    a.href = url;
    a.download = `${view.title.replace(/\s+/g, "_")}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  /* ------------------------------------------------------------------ */
  /* CLOSE                                                             */
  /* ------------------------------------------------------------------ */
  const handleClose = () => {
    navigate("/project-details", { state: { projectId } });
  };

  /* ------------------------------------------------------------------ */
  /* MEDIA                                                             */
  /* ------------------------------------------------------------------ */
  const handleAddMedia = () => {
    if (!view || !view.versions) return;
    const currentVersion = view.versions[currentVersionIndex];

    navigate("/add-media", {
      state: {
        projectId,
        viewId: view._id,
        version: currentVersion,
        html: currentVersion.html,
        device: deviceChoice,
        currentVersionIndex
      }
    });
  };

  /* ------------------------------------------------------------------ */
  /* REVISE                                                            */
  /* ------------------------------------------------------------------ */
  const handleRevise = async () => {
    if (serviceDown || !view || !view.versions) return;

    try {
      const { canGenerate, subscriptionStatus: status } = await canUserGenerate();
      setSubscriptionStatus(status);

      if (canGenerate) {
        navigate("/revise-view", {
          state: { projectId, view, currentIndex: currentVersionIndex }
        });
      } else {
        setShowLimitModal(true);
      }
    } catch (err) {
      console.error("Error checking generation permission:", err);
      navigate("/revise-view", {
        state: { projectId, view, currentIndex: currentVersionIndex }
      });
    }
  };

  /* ------------------------------------------------------------------ */
  /* DELETE VERSION                                                    */
  /* ------------------------------------------------------------------ */
  const handleVersionDelete = (versionId) => {
    const newVersions = view.versions.filter((v) => v.versionId !== versionId);
    setView({ ...view, versions: newVersions });
    setCurrentVersionIndex((idx) => (idx > 0 ? idx - 1 : 0));
  };

  /* ------------------------------------------------------------------ */
  /* SHARE                                                             */
  /* ------------------------------------------------------------------ */
  const handleShare = () => {
    setShowShareModal(true);
  };

  /* ------------------------------------------------------------------ */
  /* RESTART IFRAME                                                    */
  /* ------------------------------------------------------------------ */
  const handleRestart = () => {
    if (!view || !view.versions) return;
    setRestartLoading(true);
    setTimeout(() => {
      try {
        const iframe = document.querySelector('iframe[title="Embedded View"]');
        if (iframe) {
          const currentVersion = view.versions[currentVersionIndex];
          iframe.srcdoc = injectScrollbarStyles(currentVersion.html);
        }
      } finally {
        setRestartLoading(false);
      }
    }, 500);
  };

  // Fullscreen modal handlers
  const openFullscreen = () => {
    if (deviceChoice === "Mobile") {
      const mobileContainer = document.querySelector(`.${containerClasses.split(' ').join('.')}`);
      if (mobileContainer) {
        const width = mobileContainer.offsetWidth;
        setMobileContainerWidth(width);
      }
    }
    
    setShowFullscreen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeFullscreen = () => {
    setShowFullscreen(false);
    document.body.style.overflow = 'auto';
  };

  // Iframe hover handlers
  const handleIframeMouseEnter = () => {
    setIsIframeHovered(true);
  };

  const handleIframeMouseLeave = () => {
    setIsIframeHovered(false);
  };

  /* ------------------------------------------------------------------ */
  /* LOADING / ERROR STATES                                            */
  /* ------------------------------------------------------------------ */
  if (loading || restartLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (error === "Project ID is missing.") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Oops! That Didn't Work</h2>
          <p className="text-gray-600 mb-6">
            Looks like the action you chose could not be accomplished. Click below to go back.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center space-x-2 mx-auto"
          >
            <RefreshCcw size={20} />
            <span>Go Back</span>
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="min-h-screen flex items-center justify-center text-red-500">{error}</div>;
  }

  if (!view || !view.versions || view.versions.length === 0) {
    return <div className="min-h-screen flex items-center justify-center">No versions found.</div>;
  }

  /* ------------------------------------------------------------------ */
  /* RENDER MAIN VIEW                                                  */
  /* ------------------------------------------------------------------ */
  const currentVersion = view.versions[currentVersionIndex];
  let enhancedHtml = injectScrollbarStyles(currentVersion.html);
  enhancedHtml = injectRateLimitReporter(enhancedHtml);

  const hasMedia =
    (currentVersion.images && currentVersion.images.length > 0) ||
    (currentVersion.videos && currentVersion.videos.length > 0);

  const containerClasses =
    deviceChoice === "Mobile"
      ? "border rounded-md shadow-sm p-4 bg-white mx-auto max-w-sm"
      : "border rounded-md shadow-sm p-4 bg-white w-full";

  return (
    <div className="relative min-h-screen bg-gray-50">
      <DesktopWarningBanner isDesktop={deviceChoice === "Desktop"} />
      <Navbar5
        pageTitle={view.title}
        onDownload={handleDownload}
        onClose={handleClose}
        onRevise={handleRevise}
        onDelete={() => setShowDeleteModal(true)}
        versionIndex={currentVersionIndex}
        totalVersions={view.versions.length}
        onPrev={handlePrev}
        onNext={handleNext}
        viewId={view._id}
        onShare={handleShare}
        onRestart={handleRestart}
        onMedia={handleAddMedia}
        showMediaIcon={hasMedia}
        onTitleSave={handleTitleSave}
      />

      {showDeleteModal && (
        <DeleteVersion
          viewId={view._id}
          versionId={view.versions[currentVersionIndex].versionId}
          onClose={() => setShowDeleteModal(false)}
          onDelete={handleVersionDelete}
        />
      )}

      {showLimitModal && (
        <GenerationLimitModal
          onClose={() => setShowLimitModal(false)}
          subscriptionStatus={subscriptionStatus}
          isExpired={subscriptionStatus === "expired"}
        />
      )}

      {showShareModal && (
        <ShareModal
          itemType="view"
          itemId={view._id}
          title={view.title}
          versionId={view.versions[currentVersionIndex].versionId}
          onClose={() => setShowShareModal(false)}
          enableEnterprise={true}
        />
      )}

      <div className="max-w-8xl mx-auto p-4">
        <div 
          className={`${containerClasses} overflow-y-auto relative`} 
          style={{ height: "80vh" }}
          onMouseEnter={handleIframeMouseEnter}
          onMouseLeave={handleIframeMouseLeave}
        >
          {isIframeHovered && (
            <button
              onClick={openFullscreen}
              className="absolute top-2 right-2 z-10 p-2 bg-black/60 hover:bg-black/90 rounded-md transition-all shadow-sm"
              title="Fullscreen"
            >
              <Maximize2 className="w-5 h-5 text-slate-200" />
            </button>
          )}
          <iframe
            title="Embedded View"
            srcDoc={enhancedHtml}
            style={{ width: "100%", height: "100%", border: "none" }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals"
          />
        </div>
      </div>

      {showFullscreen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
          <div className="w-full h-full flex flex-col">
          <DesktopWarningBanner isDesktop={deviceChoice === "Desktop"} />
            <div className="bg-neutral-900 py-2 px-4 flex items-center">
              <span className="text-sm text-slate-300 flex-1 text-left">
                {view.title}
              </span>
              <button 
                onClick={closeFullscreen}
                className="text-slate-300 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {deviceChoice === "Mobile" ? (
              <div className="flex-1 bg-black flex items-center justify-center overflow-hidden">
                <div 
                  className="border rounded-md shadow-sm p-4 bg-white mx-auto" 
                  style={{ width: mobileContainerWidth ? `${mobileContainerWidth}px` : 'auto' }}
                >
                  <iframe 
                    srcDoc={enhancedHtml}
                    title="Fullscreen View"
                    className="w-full border-0"
                    style={{ height: "80vh" }}
                    sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-presentation"
                  />
                </div>
              </div>
            ) : (
              <div className="flex-1 bg-white overflow-hidden">
                <iframe 
                  srcDoc={enhancedHtml}
                  title="Fullscreen View"
                  className="w-full h-full border-0"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-presentation"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewDetails;